import type { ReactElement } from "react";
import type { StatusTone } from "../../atoms/StatusDot/index.js";
import { Link } from "../../atoms/Link/index.js";
import { safeExternalHref } from "../../lib/external-href.js";
import { TONES } from "../../lib/tones.js";

/**
 * One fact about a row, stated beside its title.
 *
 * DATA-BLIND: no wire types, no fetching, no module vocabulary. A host maps
 * whatever it knows onto this shape, and the component owns placement, chrome
 * and the anchor's safety attributes — so three render sites cannot disagree
 * about what an external link looks like or how it is named.
 *
 * ## Why a LIST rather than one badge
 *
 * A row can carry more than one independent fact. An Implementation Plan row
 * states both its PROVENANCE ("this came from a Kaizen", or "its source is
 * gone") and, once someone has sent it somewhere, its EXTERNAL WORK. Those are
 * different claims from different systems; folding them into one badge would
 * silently delete the provenance the moment a row gained a link, which is
 * exactly when a reader most needs both.
 */
export interface RowBadge {
  /** What the badge reads. Host-authored or server-supplied — never invented. */
  readonly label: string;
  /** Semantic colour. Omitted keeps the neutral outline badge that shipped. */
  readonly tone?: StatusTone;
  /** A second, quieter fact — a status word, a freshness note. */
  readonly detail?: string;
  /**
   * CANDIDATE external destination. A valid, safe URL renders `label` as an
   * external link; an invalid or unsafe one degrades to plain text — see the
   * `safeExternalHref` gate in {@link RowBadgeView}.
   */
  readonly href?: string;
  /**
   * Accessible name for the link.
   *
   * MUST begin with `label`, or speech-input users cannot say what they see
   * (WCAG 2.5.3). The component asserts this rather than trusting callers: when
   * a supplied name does not start with the visible label, it falls back to one
   * built from the label, so a careless host degrades instead of breaking.
   */
  readonly hrefLabel?: string;
}

/** Tone → the pill classes: border and text from the one tone map. Neutral
 *  reads the map's muted text like every other part (SN-04, PR 3). */
function toneClass(tone: StatusTone): string {
  return `${TONES[tone].border} ${TONES[tone].text}`;
}

/**
 * The default tone, and NOT the class the grid used to carry.
 *
 * That was `text-pb-yellow-text`, and there is no `--color-pb-yellow-text` in
 * `theme.css` — Tailwind v4 generates no rule for an undefined theme key, so it
 * emitted nothing and the badge rendered in the inherited body colour. Copying
 * it here as "the pre-change appearance, byte for byte" would have been true of
 * the grid and false of the two `vsm-feature` sites this replaced, which used
 * the real `pb-amber` tokens and an outline pill.
 *
 * (The dead class is repo-wide — ~20 call sites across `vsm-feature` and one
 * widget. Out of scope here; flagged separately.)
 */
const UNTONED = `${TONES.watch.border} ${TONES.watch.text}`;

function accessibleName(badge: RowBadge): string {
  const suffix = "(opens in a new tab)";
  const supplied = badge.hrefLabel;
  // WCAG 2.5.3: the name must start with the visible label, so a supplied
  // label that does not is repaired to the default. A valid supplied label
  // still gets the new-tab cue — the link opens a new tab regardless of who
  // named it.
  if (supplied === undefined || !supplied.startsWith(badge.label)) {
    return `${badge.label} ${suffix}`;
  }
  return supplied.endsWith(suffix) ? supplied : `${supplied} ${suffix}`;
}

/**
 * Render one badge. Shared by every site so the anchor's `rel`, its accessible
 * name and the tone mapping are decided once.
 */
export function RowBadgeView({ badge }: { badge: RowBadge }): ReactElement {
  const toned = badge.tone === undefined ? UNTONED : toneClass(badge.tone);
  // A URL that came from a provider record is DATA. `javascript:` in an `href`
  // executes on click, and nothing upstream guarantees a scheme. An address we
  // will not link to degrades to plain text — the label still tells the reader
  // what the work is.
  const href = safeExternalHref(badge.href);
  return (
    <span
      // The border WIDTH lives here, not in the tone map. Tailwind v4's preflight
      // resets every element to a zero-width border, so a tone's
      // `border-pb-*` colour paints nothing on its own — the map was documented
      // as "the pill classes" while emitting only text colour.
      // `whitespace-nowrap` keeps the label — a provider key like `PBT-9001` —
      // on one line in a narrow cell; only the quieter detail may wrap.
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-dense-2 font-medium ${toned}`}
    >
      {href === null ? (
        badge.label
      ) : (
        // `Link external` already merges `noopener noreferrer` and sets
        // `target="_blank"`, so the safety attributes are not restated here.
        <Link
          href={href}
          external
          variant="muted"
          aria-label={accessibleName(badge)}
        >
          {badge.label}
        </Link>
      )}
      {badge.detail === undefined ? null : (
        <span className="whitespace-normal text-pb-text-muted">
          · {badge.detail}
        </span>
      )}
    </span>
  );
}
